import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { getOrCreateDirectMessage, fetchDirectMessages } from '../../store/slices/channelSlice';
import { setCurrentChannel } from '../../store/slices/chatSlice';
import { fetchUsers } from '../../store/slices/userSlice';
import { addNotification } from '../../store/slices/uiSlice';

import PresignedAvatar from '../common/PresignedAvatar';
import { extractEntityId, channelMemberIds } from '../../utils/directMessageUtils';
import { formatPresenceLabel, presenceDotClass } from '../../utils/presenceLabelUtils';

const StartDirectMessageModal = ({ isOpen, onClose, onDirectMessageCreated }) => {
  const dispatch = useDispatch();
  const [, setSearchParams] = useSearchParams();
  const { users, loading: usersLoading } = useSelector((state) => state.users);
  const { directMessages } = useSelector((state) => state.channels);
  const { user: currentUser } = useSelector((state) => state.auth);
  const [searchQuery, setSearchQuery] = useState('');
  const [creatingUserId, setCreatingUserId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchUsers({ force: true, fromNetwork: true }));
      dispatch(fetchDirectMessages());
      setSearchQuery('');
    }
  }, [isOpen, dispatch]);

  const handleStartDM = async (person) => {
    const userId = extractEntityId(person);
    if (!userId) {
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: 'Could not start this conversation. Try again after users finish loading.',
      }));
      return;
    }
    setCreatingUserId(userId);
    try {
      const channel = await dispatch(getOrCreateDirectMessage(userId)).unwrap();
      // Normalize channel so ChatWindow always has id, type, memberIds
      const channelId = extractEntityId(channel) || channel.id;
      const normalizedChannel = {
        ...channel,
        id: channelId,
        _id: channel._id || channelId,
        type: channel.type || 'DIRECT_MESSAGE',
        memberIds: channelMemberIds({ ...channel, memberIds: channel.memberIds || [currentUser?.id, userId] }),
        name: channel.name,
      };

      onClose();
      // Set current channel directly so chat opens immediately (including self-chat)
      dispatch(setCurrentChannel(normalizedChannel));
      setSearchParams((prev) => {
        if (!prev.get('messageId') && !prev.get('threadId')) return prev;
        const next = new URLSearchParams(prev);
        next.delete('messageId');
        next.delete('threadId');
        return next;
      }, { replace: true });
      if (onDirectMessageCreated) {
        onDirectMessageCreated(normalizedChannel);
      }

      dispatch(addNotification({
        type: 'success',
        title: 'Success',
        message: 'Direct message started',
      }));

      dispatch(fetchDirectMessages());
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: typeof error === 'string' ? error : 'Failed to start direct message',
      }));
    } finally {
      setCreatingUserId(null);
    }
  };

  const filteredUsers = (users || []).filter((user) => {
    if (!user) return false;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
      const username = String(user.username || '').toLowerCase();
      return fullName.includes(query) || username.includes(query);
    }
    return true;
  });

  // Get existing DM user IDs
  const existingDMUserIds = directMessages
    .filter(dm => dm.type === 'DIRECT_MESSAGE')
    .flatMap(dm => dm.memberIds?.filter(id => id !== currentUser?.id) || []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Start Direct Message
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Search Input */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              autoFocus
            />
          </div>

          {/* Users List */}
          <div className="max-h-96 overflow-y-auto">
            {usersLoading ? (
              <div className="text-center text-gray-500 dark:text-gray-400 text-sm py-4">
                Loading users...
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center text-gray-500 dark:text-gray-400 text-sm py-4">
                {searchQuery ? 'No users found' : 'No users available'}
              </div>
            ) : (
              <div className="space-y-1">
                {filteredUsers.map((user) => {
                  const personId = extractEntityId(user);
                  const hasExistingDM = existingDMUserIds.includes(personId) || existingDMUserIds.includes(user.id);
                  
                  return (
                    <button
                      key={personId || user.username || user.email}
                      onClick={() => handleStartDM(user)}
                      disabled={creatingUserId === personId}
                      className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="relative flex-shrink-0">
                        <PresignedAvatar
                          fileKey={user.profilePicture}
                          name={`${user.firstName} ${user.lastName}`}
                          initials={`${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`}
                          className="w-10 h-10 rounded-full object-cover relative"
                          fallbackClassName="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center text-white text-sm font-medium relative"
                        />
                        {user.status && (
                          <div
                            className={`absolute bottom-0 right-0 w-3 h-3 border-2 border-white dark:border-gray-800 rounded-full ${presenceDotClass(user)}`}
                            title={formatPresenceLabel(user)}
                          />
                        )}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {user.firstName} {user.lastName}
                          </p>
                          {hasExistingDM && (
                            <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded">
                              Existing
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          @{user.username}
                        </p>
                      </div>
                      {creatingUserId === personId ? (
                        <svg className="w-5 h-5 animate-spin text-primary-600" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 12 5.373 12 12H4z"></path>
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartDirectMessageModal;
